import { useEffect, useMemo, useState } from 'react';
import { fetchAllAirtableRecords } from '../services/airtable';

const DEFAULT_USUARIOS_TABLE_ID = 'tblzPMTNbLb0suLaP';
const DEFAULT_ASISTENCIAS_TABLE_ID = 'tbl788wLi2KIfDYeK';

const USUARIOS_FIELD_IDS = {
  id: 'fldEtblqNUjRs6cqG',
  aprendiz: 'fldy7L9HBj6r0HF4b',
  nombre: 'fldN1lRrzE9CITnfj',
  iniciales: 'fldRCFbaH18GGpcsM',
  correo: 'fld4byHxdBUfZLT5x',
  rol: 'fldIJ3T9GwDUU82OH',
  activo: 'fld29wljjA2sckLUP',
  horarioReal: 'fldoDnaZyA3B4VD0t',
  horario: 'fldoeEEAQvzsNNvP4',
  asistencias: 'fldhB1Omj5cStBUn2',
  nombreCentroPatron: 'fldDaOVFqXPslOHAB',
  nombreComercialPatron: 'fldwilQfgQUmxrN1A',
  tutor: 'fldtof7sTqGl6MKa2',
  folioTutor: 'fldrXA0sJJxxY9JNK',
  folioCentro: 'fldytdkQ3IvaEFs4u',
  qr: 'fld3Bbb3FFTqJ6rPO'
};

const ASISTENCIAS_FIELD_IDS = {
  usuario: 'fldSD0L5M8Nc9adrF',
  fecha: 'fldtcFOYbFzi8sPoq',
  inicio: 'fld74D82fkC1ElOgW',
  fin: 'fldPJo1Q6zvEiNrX3',
  estado: 'fldXAB2mTLHZWgThN',
  metodo: 'fld1rQsnay7hwrmXm',
  validacion: 'fldpt542HVUu0jrWm',
  duracion: 'fldBAn6ljsC6xTLHZ',
  tipo: 'fldIt2bS7sorKYlZ7',
  selfie: 'fldMDvDKZVGj9uGbK'
};

const toText = value => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    const first = value.find(item => item !== undefined && item !== null && String(item).trim());
    return first === undefined ? '' : toText(first);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const text = String(value).trim();
  if (!text) return '';
  if (text.length >= 2 && text.startsWith('"') && text.endsWith('"')) {
    try {
      const parsed = JSON.parse(text);
      return typeof parsed === 'string' ? parsed.trim() : text;
    } catch {
      return text.slice(1, -1).trim();
    }
  }
  return text;
};

const toNumber = value => {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export function useAttendanceReports({
  enabled,
  usuariosTableId = DEFAULT_USUARIOS_TABLE_ID,
  asistenciasTableId = DEFAULT_ASISTENCIAS_TABLE_ID,
  refreshIndex = 0
}) {
  const [users, setUsers] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setUsers([]);
      setRecords([]);
      setLoading(false);
      setError('');
      return;
    }

    let ignore = false;
    async function loadAttendanceData() {
      try {
        setLoading(true);
        setError('');

        const [usuariosRaw, asistenciasRaw] = await Promise.all([
          fetchAllAirtableRecords({
            tableId: usuariosTableId,
            maxRecords: 1000,
            returnFieldsByFieldId: true,
            fields: Object.values(USUARIOS_FIELD_IDS)
          }),
          fetchAllAirtableRecords({
            tableId: asistenciasTableId,
            maxRecords: 5000,
            returnFieldsByFieldId: true,
            fields: Object.values(ASISTENCIAS_FIELD_IDS)
          })
        ]);

        if (ignore) return;

        const normalizedUsers = (usuariosRaw || []).map(record => {
          const fields = record.fields || {};
          return {
            id: record.id,
            clave: toText(fields[USUARIOS_FIELD_IDS.id]),
            nombre: toText(fields[USUARIOS_FIELD_IDS.nombre]) || toText(fields[USUARIOS_FIELD_IDS.aprendiz]),
            iniciales: toText(fields[USUARIOS_FIELD_IDS.iniciales]),
            correo: toText(fields[USUARIOS_FIELD_IDS.correo]),
            rol: toText(fields[USUARIOS_FIELD_IDS.rol]),
            activo: toText(fields[USUARIOS_FIELD_IDS.activo]),
            horario: toText(fields[USUARIOS_FIELD_IDS.horarioReal]) || toText(fields[USUARIOS_FIELD_IDS.horario]),
            asistenciasIds: Array.isArray(fields[USUARIOS_FIELD_IDS.asistencias])
              ? fields[USUARIOS_FIELD_IDS.asistencias]
              : [],
            nombreCentroPatron: toText(fields[USUARIOS_FIELD_IDS.nombreCentroPatron]),
            nombreComercialPatron: toText(fields[USUARIOS_FIELD_IDS.nombreComercialPatron]),
            tutor: toText(fields[USUARIOS_FIELD_IDS.tutor]),
            folioTutor: toText(fields[USUARIOS_FIELD_IDS.folioTutor]),
            folioCentro: toText(fields[USUARIOS_FIELD_IDS.folioCentro]),
            qr: fields[USUARIOS_FIELD_IDS.qr] || []
          };
        });

        const normalizedRecords = (asistenciasRaw || [])
          .map(record => {
            const fields = record.fields || {};
            const linkedUsers = fields[ASISTENCIAS_FIELD_IDS.usuario];
            const usuarioId = Array.isArray(linkedUsers) ? linkedUsers[0] : '';
            return {
              id: record.id,
              usuarioId,
              fecha: toText(fields[ASISTENCIAS_FIELD_IDS.fecha]),
              inicio: toText(fields[ASISTENCIAS_FIELD_IDS.inicio]),
              fin: toText(fields[ASISTENCIAS_FIELD_IDS.fin]),
              estado: toText(fields[ASISTENCIAS_FIELD_IDS.estado]),
              metodo: toText(fields[ASISTENCIAS_FIELD_IDS.metodo]),
              validacion: toText(fields[ASISTENCIAS_FIELD_IDS.validacion]),
              duracionMin: toNumber(fields[ASISTENCIAS_FIELD_IDS.duracion]),
              tipo: toText(fields[ASISTENCIAS_FIELD_IDS.tipo]) || 'Asistencia',
              selfie: fields[ASISTENCIAS_FIELD_IDS.selfie] || []
            };
          })
          .filter(item => item.usuarioId);

        setUsers(normalizedUsers);
        setRecords(normalizedRecords);
      } catch (loadError) {
        if (ignore) return;
        setError(loadError.message || 'No fue posible cargar asistencias desde Airtable.');
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadAttendanceData();
    return () => {
      ignore = true;
    };
  }, [enabled, usuariosTableId, asistenciasTableId, refreshIndex]);

  const usersById = useMemo(() => {
    const directory = {};
    users.forEach(user => {
      directory[user.id] = user;
    });
    return directory;
  }, [users]);

  return {
    users,
    usersById,
    records,
    loading,
    error
  };
}
